'use client'

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '@/i18n/client'

interface ExternalLinkProps {
  href: string
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

/**
 * DSGVO-konformer externer Link.
 * Zeigt vor Weiterleitung einen Hinweis-Dialog an (Datenschutz + Haftungsausschluss).
 * Identisches Design wie der Dialog in flying-fields-map.tsx.
 */
export function ExternalLink({ href, children, className, style }: ExternalLinkProps) {
  const { t } = useTranslation()
  const [showDialog, setShowDialog] = useState(false)

  // Dienst-Name aus URL extrahieren
  const serviceName = (() => {
    try {
      const host = new URL(href).hostname.replace('www.', '')
      return host
    } catch {
      return t('legal.externalLink.fallbackServiceName')
    }
  })()

  return (
    <>
      <a
        href={href}
        onClick={(e) => { e.preventDefault(); setShowDialog(true) }}
        className={className}
        style={style}
      >
        {children}
      </a>

      {showDialog && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowDialog(false) }}
        >
          <div style={{
            background: 'var(--bg-card, #1a1a2e)',
            border: '2px solid rgba(242,201,78,0.4)',
            borderRadius: '16px',
            padding: '20px',
            maxWidth: '360px',
            width: '92%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            maxHeight: '90vh',
            overflowY: 'auto' as const,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>🔗</span>
              <span style={{ fontSize: '14px', fontWeight: 900, color: '#f2c94e', textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>
                {t('legal.externalLink.dialogTitle')}
              </span>
            </div>
            <p
              style={{ fontSize: '12px', color: 'var(--text, #ccc)', lineHeight: '1.6', marginBottom: '8px' }}
              dangerouslySetInnerHTML={{ __html: t('legal.externalLink.intro', { service: serviceName }) }}
            />
            <div style={{ fontSize: '11px', color: 'var(--muted, #999)', lineHeight: '1.6', marginBottom: '10px', paddingLeft: '8px', borderLeft: '2px solid rgba(242,201,78,0.25)' }}>
              <p style={{ marginBottom: '6px' }}>
                {t('legal.externalLink.providerNote')}
              </p>
              <p>
                {t('legal.externalLink.liabilityNote')}
              </p>
            </div>
            <p style={{ fontSize: '9px', color: 'var(--muted, #666)', marginBottom: '12px', fontStyle: 'italic' }}>
              {t('legal.externalLink.legalRef')}
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowDialog(false)}
                style={{ flex: 1, padding: '8px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: 800, background: 'rgba(255,255,255,0.06)', color: 'var(--muted, #999)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
              >
                {t('legal.externalLink.cancel')}
              </button>
              <button
                onClick={() => { window.open(href, '_blank', 'noopener,noreferrer'); setShowDialog(false) }}
                style={{ flex: 1, padding: '8px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: 800, background: 'rgba(242,201,78,0.15)', color: '#f2c94e', border: '1px solid rgba(242,201,78,0.4)', cursor: 'pointer' }}
              >
                {t('legal.externalLink.continueTo', { service: serviceName })}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
